import { Lock, Shield, Eye, Trash2, Download } from "lucide-react";
import { Footer } from "../components/layout/Footer";

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-cream pb-24 md:pb-8" data-testid="privacy-page">
      <header className="bg-teal-700 text-white px-6 pt-8 pb-6">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-3xl font-serif flex items-center gap-3"><Lock size={28} /> Privacy Policy</h1>
          <p className="text-teal-200 text-sm mt-2">GDPR Compliant • Last updated: March 2026</p>
        </div>
      </header>

      <div className="px-6 py-8 max-w-4xl mx-auto">
        {/* GDPR Quick Summary */}
        <div className="bg-teal-50 border border-teal-200 rounded-2xl p-6 mb-6">
          <h2 className="text-lg font-semibold text-teal-800 flex items-center gap-2"><Shield size={20} /> Your Rights at a Glance</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            <div className="flex items-start gap-3">
              <Eye size={18} className="text-teal-600 mt-0.5" />
              <div>
                <p className="font-medium text-teal-900">Right to Access</p>
                <p className="text-sm text-teal-700">Request a copy of your personal data</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Trash2 size={18} className="text-teal-600 mt-0.5" />
              <div>
                <p className="font-medium text-teal-900">Right to Erasure</p>
                <p className="text-sm text-teal-700">Request deletion of your data</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Download size={18} className="text-teal-600 mt-0.5" />
              <div>
                <p className="font-medium text-teal-900">Data Portability</p>
                <p className="text-sm text-teal-700">Export your data in a portable format</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-6 border border-stone-100 space-y-6">
          <section>
            <h2 className="text-xl font-serif text-stone-900 mb-3">1. Introduction</h2>
            <p className="text-stone-600">
              OspitAI ("we", "us", or "our") respects your privacy and is committed to protecting your personal data. 
              This privacy policy explains how we collect, use, and safeguard your information when you use our Service.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-serif text-stone-900 mb-3">2. Data Controller</h2>
            <p className="text-stone-600">
              OspitAI acts as the data controller for personal data collected through our platform. 
              For GDPR purposes, our contact details are: privacy@ospitai.com
            </p>
          </section>

          <section>
            <h2 className="text-xl font-serif text-stone-900 mb-3">3. Data We Collect</h2>
            <p className="text-stone-600 mb-2">We collect the following categories of data:</p>
            <ul className="list-disc list-inside text-stone-600 space-y-1">
              <li><strong>Account Data:</strong> Email, password (hashed), property information for hosts</li>
              <li><strong>Booking Data:</strong> Guest name, email, phone (optional), booking preferences</li>
              <li><strong>Chat Data:</strong> Conversations with our AI concierge (anonymized for improvement)</li>
              <li><strong>Technical Data:</strong> IP address, browser type, device information</li>
              <li><strong>Cookie Data:</strong> Preferences, session data, analytics (with consent)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-serif text-stone-900 mb-3">4. Legal Basis for Processing</h2>
            <p className="text-stone-600 mb-2">We process your data based on:</p>
            <ul className="list-disc list-inside text-stone-600 space-y-1">
              <li><strong>Contract:</strong> To provide our services (bookings, recommendations)</li>
              <li><strong>Consent:</strong> For analytics and marketing communications</li>
              <li><strong>Legitimate Interest:</strong> To improve our service and prevent fraud</li>
              <li><strong>Legal Obligation:</strong> To comply with applicable laws</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-serif text-stone-900 mb-3">5. How We Use Your Data</h2>
            <ul className="list-disc list-inside text-stone-600 space-y-1">
              <li>To provide and improve our concierge services</li>
              <li>To process bookings and payments</li>
              <li>To send booking confirmations and service updates</li>
              <li>To respond to your inquiries and provide support</li>
              <li>To analyze usage patterns and improve user experience (with consent)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-serif text-stone-900 mb-3">6. Data Sharing</h2>
            <p className="text-stone-600 mb-2">We share data only with:</p>
            <ul className="list-disc list-inside text-stone-600 space-y-1">
              <li><strong>Payment Processors:</strong> Stripe (for secure payment handling)</li>
              <li><strong>AI Providers:</strong> OpenAI (for concierge functionality, anonymized)</li>
              <li><strong>Property Hosts:</strong> Relevant booking information only</li>
              <li><strong>Service Providers:</strong> Third parties who perform services on our behalf</li>
            </ul>
            <p className="text-stone-600 mt-2">We never sell your personal data to third parties.</p>
          </section>

          <section>
            <h2 className="text-xl font-serif text-stone-900 mb-3">7. Data Retention</h2>
            <p className="text-stone-600">
              We retain your data only as long as necessary for the purposes outlined in this policy:
            </p>
            <ul className="list-disc list-inside text-stone-600 mt-2 space-y-1">
              <li>Account data: Until account deletion + 30 days</li>
              <li>Booking data: 7 years (for legal and tax compliance)</li>
              <li>Chat data: 90 days (then anonymized for AI improvement)</li>
              <li>Analytics data: 26 months</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-serif text-stone-900 mb-3">8. Your GDPR Rights</h2>
            <p className="text-stone-600 mb-2">Under GDPR, you have the right to:</p>
            <ul className="list-disc list-inside text-stone-600 space-y-1">
              <li><strong>Access:</strong> Request a copy of your personal data</li>
              <li><strong>Rectification:</strong> Request correction of inaccurate data</li>
              <li><strong>Erasure:</strong> Request deletion of your data ("right to be forgotten")</li>
              <li><strong>Portability:</strong> Receive your data in a machine-readable format</li>
              <li><strong>Objection:</strong> Object to processing based on legitimate interests</li>
              <li><strong>Withdraw Consent:</strong> Withdraw consent at any time for consent-based processing</li>
            </ul>
            <p className="text-stone-600 mt-2">
              To exercise these rights, contact us at privacy@ospitai.com or use the tools in your account settings.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-serif text-stone-900 mb-3">9. Cookies</h2>
            <p className="text-stone-600">
              We use cookies for essential functionality and, with your consent, for analytics. 
              You can manage your preferences through our cookie banner or browser settings.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-serif text-stone-900 mb-3">10. Security</h2>
            <p className="text-stone-600">
              We implement appropriate technical and organizational measures to protect your data, including 
              encryption in transit (TLS), secure password hashing, and regular security assessments.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-serif text-stone-900 mb-3">11. International Transfers</h2>
            <p className="text-stone-600">
              Your data may be processed outside the EEA by our service providers. We ensure adequate protection 
              through Standard Contractual Clauses or adequacy decisions.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-serif text-stone-900 mb-3">12. Contact & Complaints</h2>
            <p className="text-stone-600">
              For privacy inquiries: privacy@ospitai.com<br />
              You also have the right to lodge a complaint with your local data protection authority.
            </p>
          </section>
        </div>
      </div>
      <Footer />
    </div>
  );
}
